import { Key, ReactNode } from 'react';
import { TmdxAvatar, TmdxChip, Tooltip } from '@ds';
import { getLocalTimeZone } from '@internationalized/date';
import { ActionMenu, MenuItemType } from '@ui';
import { MdOutlineMoreHoriz } from 'react-icons/md';
import {
  accessColumns,
  CaseAccessRoleType,
  getAccessStatusLabel,
  getCaseAccessRoleLabel,
  getCaseAccessRoleTypes,
  getUserDisplayName,
} from './config';
import { formatDate } from '@/utils/helpers';
import { CaseAccessTicketItem, CaseRoleOnCase, CaseUserGroup } from '@/api/apis/case';
import { EMPLOYEE_ROLE_NAMES } from '@/constants/user.constants';
import { roleByCaseUserGroup } from '@/constants/case/case.constants';

export type CaseAccessActions = {
  onGrant90: (item: CaseAccessTicketItem) => void;
  onGrantIndefinite: (item: CaseAccessTicketItem) => void;
  onRevoke: (item: CaseAccessTicketItem) => void;
};

export type CaseAccessLoadingState = { userId: string; action: 'grant90' | 'grantIndefinite' | 'revoke' } | null;

const ROLE_ABBREVIATIONS: Partial<Record<CaseUserGroup, string>> = {
  [CaseUserGroup.OrganManagementSpecialists]: 'OMS',
};

const getCaseUserGroupLabel = (group: CaseUserGroup) => {
  if (ROLE_ABBREVIATIONS[group]) {
    return ROLE_ABBREVIATIONS[group];
  }

  const role = roleByCaseUserGroup[group]!;
  return EMPLOYEE_ROLE_NAMES[role];
};

const renderEmptyValue = () => <span className="text-m-r">-</span>;

const renderCaseRoles = (roles?: CaseRoleOnCase[] | null) => {
  if (!roles?.length) return renderEmptyValue();

  const omsIndex = roles.findIndex((role) => role.group === CaseUserGroup.OrganManagementSpecialists);
  const ordered = omsIndex >= 0 ? [roles[omsIndex], ...roles.filter((_, index) => index !== omsIndex)] : roles;

  const mapped = ordered
    .map((role) => ({
      label: getCaseUserGroupLabel(role.group),
      isPrimary: Boolean(role.isPrimary),
    }))
    .filter((role) => Boolean(role.label));

  if (!mapped.length) return renderEmptyValue();

  return (
    <div className="flex items-center gap-2 truncate">
      {mapped.map((role, index) => {
        if (index === 0) {
          return (
            <div key={`main-${role.label}`} className="flex items-center gap-2">
              <TmdxChip size="sm">{role.label}</TmdxChip>

              {role.isPrimary && (
                <TmdxChip size="sm" color="success">
                  Primary
                </TmdxChip>
              )}
            </div>
          );
        }

        if (index === 1) {
          const rest = mapped.slice(index);
          const tooltipText = rest.map((item) => (item.isPrimary ? `${item.label} (Primary)` : item.label)).join(', ');

          return (
            <Tooltip placement="top" className="max-w-64" content={tooltipText} key="additionalRolesCount">
              <TmdxChip size="sm">{`+${mapped.length - index}`}</TmdxChip>
            </Tooltip>
          );
        }

        return null;
      })}
    </div>
  );
};

const getAccessExpirationLabel = ({ status, isInfinite, expiresAt }: CaseAccessTicketItem) => {
  if (status !== 'Active') return '-';
  if (isInfinite) return 'Never';
  if (!expiresAt) return '-';

  return formatDate(expiresAt, 'MM/dd/yyyy', { timezone: getLocalTimeZone() });
};

const getAccessActionsMenu = (
  item: CaseAccessTicketItem,
  actions: CaseAccessActions,
  roleTypes: CaseAccessRoleType[],
): MenuItemType[] => {
  const hasOmsRole = roleTypes.includes(CaseAccessRoleType.OMS);

  const items: MenuItemType[] = [
    {
      key: 'grant-90',
      type: 'action',
      to: 'grant-90',
      label: <span className="text-m-r">60-days</span>,
      onAction: () => actions.onGrant90(item),
    },
  ];

  if (hasOmsRole) {
    items.push({
      key: 'grant-indefinite',
      type: 'action',
      to: 'grant-indefinite',
      label: <span className="text-m-r">Indefinite</span>,
      onAction: () => actions.onGrantIndefinite(item),
    });
  }

  items.push({
    key: 'revoke',
    type: 'action',
    to: 'revoke',
    label: <span className="text-m-r text-danger">Revoke</span>,
    onAction: () => actions.onRevoke(item),
  });

  return items;
};

export const getRenderCaseAccessTableCell =
  ({
    actions,
    loading,
    canGrant90,
    canGrantIndefinite,
    canRevoke,
  }: {
    actions: CaseAccessActions;
    loading: CaseAccessLoadingState;
    canGrant90: boolean;
    canGrantIndefinite: boolean;
    canRevoke: boolean;
  }) =>
  (item: CaseAccessTicketItem, columnKey: Key): ReactNode => {
    const { forUser, status, caseRoles, isInfinite } = item;

    const displayName = getUserDisplayName(forUser);
    const roleTypes = getCaseAccessRoleTypes(item);
    const hasOmsRole = roleTypes.includes(CaseAccessRoleType.OMS);
    const accessExpirationLabel = getAccessExpirationLabel(item);

    switch (columnKey) {
      case accessColumns.name.key:
        return (
          <div className="flex items-center gap-2">
            <div className="flex flex-col shrink-0 items-center overflow-hidden">
              <TmdxAvatar className="shrink-0" size="lg" radius="lg" name={displayName} src={forUser.photoPath} />
            </div>

            <div>
              <div className="text-m-m">{displayName}</div>
            </div>
          </div>
        );

      case accessColumns.userRole.key:
        return <span className="text-m-r">{getCaseAccessRoleLabel(item)}</span>;

      case accessColumns.roleOnCase.key:
        if (!hasOmsRole) {
          return renderEmptyValue();
        }

        return renderCaseRoles(caseRoles);

      case accessColumns.accessStatus.key:
        return <span className="text-m-r">{getAccessStatusLabel(status)}</span>;

      case accessColumns.accessExpirationDate.key:
        return <span className="text-m-r">{accessExpirationLabel}</span>;

      case accessColumns.grantAccess.key: {
        const hasActive = status === 'Active';
        const hasIndefinite = hasActive && isInfinite;
        const isCurrentRowLoading = loading?.userId === forUser.id;

        const grant90Disabled = !canGrant90 || hasIndefinite;
        const grantIndefDisabled = !hasOmsRole || !canGrantIndefinite || hasIndefinite;
        const revokeDisabled = !canRevoke || !hasActive;

        const items = getAccessActionsMenu(item, actions, roleTypes);

        const disabledKeys = [
          grant90Disabled ? 'grant-90' : null,
          grantIndefDisabled ? 'grant-indefinite' : null,
          revokeDisabled ? 'revoke' : null,
        ].filter(Boolean) as string[];

        const allDisabled = items.every((menuItem) => disabledKeys.includes(String(menuItem.key)));

        return (
          <div className="flex w-full justify-start">
            <ActionMenu
              placement="left-start"
              items={items}
              disabledKeys={disabledKeys}
              isDisabled={allDisabled || isCurrentRowLoading}
              MenuTrigger={<MdOutlineMoreHoriz size={20} />}
              label={`${displayName} access actions menu`}
            />
          </div>
        );
      }

      default:
        return null;
    }
  };
